import { ErrorPage, ErrorPageType } from '@exam-management/components';
import { useGetTaskCommonInfoQuery, WorkflowCode } from '@exam-management/graphql';
import { useAuth } from '@shared/auth';
import { Loading } from '@shared/components';
import type { PropsWithChildren } from '@shared/utils';
import { matchPath, Navigate, useLocation } from 'react-router-dom';

const PERMISSION_MAPPER = {
  [WorkflowCode.QuestionCreation]: 'question-creation',
  [WorkflowCode.QuestionReview]: 'question-review',
  [WorkflowCode.ReviewQSet]: 'qset-review',
};

interface TaskManagementPermissionRouteProps extends PropsWithChildren {
  taskId: string;
}

export default function TaskManagementPermissionRoute(
  props: Readonly<TaskManagementPermissionRouteProps>,
) {
  const { taskId, children } = props;
  const pathname = useLocation().pathname;

  const {
    data,
    loading: isLoading,
    error,
  } = useGetTaskCommonInfoQuery({
    variables: {
      getTaskByIdInput: {
        taskId,
      },
    },
    fetchPolicy: 'network-only',
  });
  const auth = useAuth();
  const userId = auth.userProfile?.id;

  const { assignee, ownerId = '', currentWorkflowStep } = data?.wflGetTaskById?.task ?? {};
  // const { id: assigneeId = '' } = assignee ?? {};
  const { workflowCode = '' } = currentWorkflowStep ?? {};

  if (isLoading) {
    return <Loading sx={{ height: '100dvh' }} />;
  }
  if (error) {
    return <ErrorPage errorType={ErrorPageType.NO_PERMISSION} />;
  }
  // TODO: คิดว่าผลักไป 404 page
  if (!userId || !workflowCode) {
    return null;
  }

  const workflowPath = PERMISSION_MAPPER[workflowCode as keyof typeof PERMISSION_MAPPER];
  const matchedWorkflowCode = matchPath(`/:any/${workflowPath}/*`, pathname);
  if (!matchedWorkflowCode) {
    // TODO: คิดว่าผลักไป 404 page
    return null;
  }

  const matchedMyTask = matchPath('/my-task/*', pathname);
  const matchedTaskManagement = matchPath('/task-management/*', pathname);
  if (matchedMyTask) {
    const matchMyTaskQSetReviewViewSummary = matchPath(
      '/my-task/qset-review/:taskId/qset/:qSetId/*',
      pathname,
    );
    if (matchMyTaskQSetReviewViewSummary) {
      // main task จะเก็บแค่ owner กับ lead reviewer และหน้านี้จะเห็นแค่เฉพาะ owner และ lead reviewer เท่านั้น
      const hasPermission = userId === ownerId || userId === assignee?.id;
      if (!hasPermission) {
        // TODO: access denied
        return <Navigate to='/access-denied' replace />;
      }
    }

    const matchMyTaskQuestionCreation = matchPath('/my-task/question-creation/:taskId/*', pathname);
    if (matchMyTaskQuestionCreation) {
      // Only assignee can access their own question creation task
      const hasAccess = userId === assignee?.id;
      if (!hasAccess) {
        return <Navigate to='/access-denied' replace />;
      }
    }

    const matchMyTaskQuestionReview = matchPath('/my-task/question-review/:taskId/*', pathname);
    if (matchMyTaskQuestionReview) {
      // Only assignee of subtask can access their own question review task
      const hasAccess = userId === assignee?.id;
      if (!hasAccess) {
        return <Navigate to='/access-denied' replace />;
      }
    }

    // if (userId === assignerId || userId === assigneeId) {
    //   return children;
    // } else {
    //   // TODO: คิดว่าผลักไป 404 page
    //   return null;
    // }
    // If the user is assignee of subtask, they should be able to access the task detail page in my-task route, even if they are not assigner or assignee of the main task
    return children;
  } else if (matchedTaskManagement) {
    if (userId === ownerId) {
      return children;
    } else {
      // TODO: คิดว่าผลักไป 404 page
      return null;
    }
  }
  return null;
}
